import { useEffect, useRef } from 'react';
import { SVG } from '@svgdotjs/svg.js';

import useStore from '../../store/store';
import { FixedNumber } from '../../utility';
import { useModal } from '../../ModalContext';

const FullRuler = () => {
	const groupRef = useRef();
	const distance = useStore((state) => state.distance);
	const setDistance = useStore((state) => state.setDistance);
	const getSoundSourcePosition = useStore((state) => state.getSoundSourcePosition);
	const getUnitFromMeter = useStore((state) => state.getUnitFromMeter);
	const triggerModal = useModal();

	useEffect(() => {
		const draw = SVG('#rulers');

		const gParent = draw.group();
		const { x, y } = getSoundSourcePosition();
		gParent.transform({ translateX: x, translateY: y });

		const g = groupRef.current = gParent.group().attr({ class: 'clickable' }).transform({ translateY: 50 });
		g.polyline().stroke({ color: '#ffffff', width: '2' }).id('line');
		g.text().font({ anchor: 'middle', fill: '#ffffff' }).id('text');
		g.rect().fill('transparent').transform({ translate: { y: -5 } }).id('rect');
	}, []);

	useEffect(() => {
		groupRef.current.findOne('#line')
			.plot(`0,0 ${getUnitFromMeter(distance)},0`);
		groupRef.current.findOne('#text')
			.text(`${distance}m`)
			.font({ fill: '#ffffff' })
			.transform({ translate: { x: getUnitFromMeter(distance) / 2, y: 18 } });
		groupRef.current.findOne('#rect').attr({
			width: getUnitFromMeter(distance),
			height: '12px',
		});
	}, [distance]);

	useEffect(() => {
		const onSave = (newValue) => setDistance(newValue);

		groupRef.current.click(() => triggerModal(
			'D:',
			distance,
			onSave,
			groupRef.current.findOne('#text').node
		));
		return () => groupRef.current.click(null);
	}, [distance]);

	return null;
};

const SourceToBarrierRuler = () => {
	const gParentRef = useRef();
	const sourceToFirstBarrierGroup = useRef();
	const triggerModal = useModal();

	// store refs
	const getSoundSourcePosition = useStore((state) => state.getSoundSourcePosition);
	const distance = useStore((state) => state.distance);
	const getUnitFromMeter = useStore((state) => state.getUnitFromMeter);
	const barrierXpos = useStore((state) =>
		Number(state.barrier1.distance) <= Number(state.barrier2.distance) || state.scenario === 'singleBarrier'
			? state.barrier1.distance : state.barrier2.distance
	);
	const setGroundDistances = useStore((state) => state.setGroundDistances);

	// Position component
	useEffect(() => {
		const draw = SVG('#rulers');
		const { x, y } = getSoundSourcePosition();
		const gParent = gParentRef.current = draw.group();
		gParent.transform({ translateX: x, translateY: y });

		const g = gParent.group().transform({ translateY: 30 });

		const g1 = sourceToFirstBarrierGroup.current = g.group().attr({ class: 'clickable' });
		g1.text().font({ anchor: 'middle', fill: '#ffffff' }).id('text');
		const lineGroup = g1.group().transform({ rotate: -90 }).id('line-group1');
		lineGroup.polyline().stroke({ color: '#ffffff', width: 1 }).id('line');
		lineGroup.polygon('0 0 -3 5 3 5').transform({ translateY: -1, scale: 0.8 }).fill('#ffffff').id('left-arrow');
		lineGroup.polygon('0 0 -3 -5 3 -5').transform({ translateY: 1, scale: 0.8 }).fill('#ffffff').id('right-arrow');
		g1.rect().fill('transparent').id('rect');

		return () => {
			gParent.remove();
		};
	}, []);

	// Update component
	useEffect(() => {
		sourceToFirstBarrierGroup.current.findOne('#line').plot(`0,0 0,${getUnitFromMeter(barrierXpos)}`);
		sourceToFirstBarrierGroup.current.findOne('#text').text(`${FixedNumber(barrierXpos)}m`).transform({ translate: { x: getUnitFromMeter(barrierXpos) / 2, y: -10 } });
		sourceToFirstBarrierGroup.current.findOne('#right-arrow').transform({ scale: 0.8, translateY: getUnitFromMeter(barrierXpos) + 1 });
		sourceToFirstBarrierGroup.current.findOne('#rect')
			.attr({
				width: getUnitFromMeter(barrierXpos),
				height: '12px',
			})
			.transform({ translate: { y: -6 } });
	}, [distance, barrierXpos]);

	useEffect(() => {
		const onSave = (newValue) => {
			setGroundDistances({ sourceToBarrier: newValue });
		};

		sourceToFirstBarrierGroup.current.click(() => triggerModal(
			'D:',
			FixedNumber(barrierXpos),
			onSave,
			sourceToFirstBarrierGroup.current.findOne('#text').node
		));

		return () => sourceToFirstBarrierGroup.current.click(null);
	}, [sourceToFirstBarrierGroup.current, barrierXpos]);

	return null;
}
const BarrierToBarrierRuler = () => {
	// element refs
	const gParentRef = useRef();
	const barrierToBarrierGroup = useRef();
	const triggerModal = useModal();

	// store refs
	const getSoundSourcePosition = useStore((state) => state.getSoundSourcePosition);
	const distance = useStore((state) => state.distance);
	const getUnitFromMeter = useStore((state) => state.getUnitFromMeter);
	const [closeBarrierX, farBarrierX] = useStore((state) =>
		Number(state.barrier1.distance) > Number(state.barrier2.distance)
			? [state.barrier2.distance, state.barrier1.distance] : [state.barrier1.distance, state.barrier2.distance]
	);
	const setGroundDistances = useStore((state) => state.setGroundDistances);

	// Position component
	useEffect(() => {
		const draw = SVG('#rulers');
		const { x, y } = getSoundSourcePosition();
		const gParent = gParentRef.current = draw.group();
		gParent.transform({ translateX: x, translateY: y });

		const g = gParent.group().transform({ translateY: 30 });

		const g2 = barrierToBarrierGroup.current = g.group()
			.attr({ class: 'clickable' })
			.transform({ translateX: getUnitFromMeter(closeBarrierX) });
		g2.text().font({ anchor: 'middle', fill: '#ffffff' }).id('text');
		const lineGroup2 = g2.group().transform({ rotate: -90 }).id('line-group');
		lineGroup2.polyline().stroke({ color: '#ffffff', width: '1' }).id('line');
		lineGroup2.polygon('0 0 -3 5 3 5').transform({ translateY: -1, scale: 0.8 }).fill('#ffffff').id('left-arrow');
		lineGroup2.polygon('0 0 -3 -5 3 -5').transform({ translateY: 1, scale: 0.8 }).fill('#ffffff').id('right-arrow');
		g2.rect().fill('transparent').id('rect');

		return () => {
			gParent.remove();
		};
	}, []);

	// Update component
	useEffect(() => {
		barrierToBarrierGroup.current.transform({ translateX: getUnitFromMeter(closeBarrierX) });
		barrierToBarrierGroup.current.findOne('#line').plot(`0,0 0,${getUnitFromMeter(farBarrierX - closeBarrierX)}`);
		barrierToBarrierGroup.current.findOne('#text').text(`${FixedNumber(farBarrierX - closeBarrierX)}m`).transform({ translate: { x: getUnitFromMeter(farBarrierX - closeBarrierX) / 2, y: -10 } });
		barrierToBarrierGroup.current.findOne('#right-arrow').transform({ scale: 0.8, translateY: getUnitFromMeter(farBarrierX - closeBarrierX) + 1 });
		barrierToBarrierGroup.current.findOne('#rect')
			.attr({
				width: getUnitFromMeter(farBarrierX - closeBarrierX),
				height: '12px',
			})
			.transform({ translate: { y: -6 } });
	}, [distance, closeBarrierX, farBarrierX]);

	useEffect(() => {
		const onSave = (newValue) => {
			setGroundDistances({ barrierToBarrier: newValue });
		};

		barrierToBarrierGroup.current.click(() => triggerModal(
			'D:',
			FixedNumber(farBarrierX - closeBarrierX),
			onSave,
			barrierToBarrierGroup.current.findOne('#text').node
		));
		return () => barrierToBarrierGroup.current.click(null);
	}, [barrierToBarrierGroup.current, farBarrierX, closeBarrierX]);

	return null;
};

const BarrierToReceiver = () => {
	// element refs
	const gParentRef = useRef();
	const secondBarrierToReceiverGroup = useRef();
	const triggerModal = useModal();

	// store refs
	const getSoundSourcePosition = useStore((state) => state.getSoundSourcePosition);
	const distance = useStore((state) => state.distance);
	const getUnitFromMeter = useStore((state) => state.getUnitFromMeter);
	const barrierXpos = useStore((state) =>
		Number(state.barrier1.distance) > Number(state.barrier2.distance) || state.scenario === 'singleBarrier'
			? state.barrier1.distance : state.barrier2.distance
	);
	const setGroundDistances = useStore((state) => state.setGroundDistances);

	// Position component
	useEffect(() => {
		const draw = SVG('#rulers');
		const { x, y } = getSoundSourcePosition();
		const gParent = gParentRef.current = draw.group();
		gParent.transform({ translateX: x, translateY: y });

		const g = gParent.group().transform({ translateY: 30 });

		const g2 = secondBarrierToReceiverGroup.current = g.group()
			.attr({ class: 'clickable' })
			.transform({ translateX: getUnitFromMeter(barrierXpos) });
		g2.text().font({ anchor: 'middle' }).id('text');
		const lineGroup2 = g2.group().transform({ rotate: -90 }).id('line-group');
		lineGroup2.polyline().stroke({ color: '#ffffff', width: '1' }).id('line');
		lineGroup2.polygon('0 0 -3 5 3 5').transform({ translateY: -1, scale: 0.8 }).fill('#ffffff').id('left-arrow');
		lineGroup2.polygon('0 0 -3 -5 3 -5').transform({ translateY: 1, scale: 0.8 }).fill('#ffffff').id('right-arrow');
		g2.rect().fill('transparent').id('rect');

		return () => {
			gParent.remove();
		};
	}, []);

	// Update component
	useEffect(() => {
		secondBarrierToReceiverGroup.current.transform({ translateX: getUnitFromMeter(barrierXpos) });
		secondBarrierToReceiverGroup.current.findOne('#line').plot(`0,0 0,${getUnitFromMeter(distance - barrierXpos)}`);
		secondBarrierToReceiverGroup.current.findOne('#text').text(`${FixedNumber(distance - barrierXpos)}m`).transform({ translate: { x: getUnitFromMeter(distance - barrierXpos) / 2, y: -10 } }).fill('#ffffff');
		secondBarrierToReceiverGroup.current.findOne('#right-arrow').transform({ scale: 0.8, translateY: getUnitFromMeter(distance - barrierXpos) + 1 });
		secondBarrierToReceiverGroup.current.findOne('#rect')
			.attr({
				width: getUnitFromMeter(distance - barrierXpos),
				height: '12px',
			})
			.transform({ translate: { y: -6 } });
	}, [distance, barrierXpos]);

	useEffect(() => {
		const onSave = (newValue) => {
			setGroundDistances({ barrierToReceiver: newValue });
		};

		secondBarrierToReceiverGroup.current.click(() => triggerModal(
			'D:',
			FixedNumber(distance - barrierXpos),
			onSave,
			secondBarrierToReceiverGroup.current.findOne('#text').node
		));
		return () => secondBarrierToReceiverGroup.current.click(null);
	}, [secondBarrierToReceiverGroup.current, distance, barrierXpos]);

	return null;
}

const Ground = () => {
	const viewBoxWidth = useStore((state) => state.view.width);
	const groundHeight = useStore((state) => state.groundHeight);
	const scenario = useStore((state) => state.scenario);
	const getGroundY = useStore((state) => state.getGroundY);

	useEffect(() => {
		const draw = SVG('#ground');

		const g = draw.group();
		g.transform({ translateX: 0, translateY: getGroundY() });
		//  #ef7521
		//  #FF895C
		//  #2F4858

		g.rect(viewBoxWidth, groundHeight).fill('#ef7521');
	}, []);

	return (
		<>
			<g id="ground"></g>
			<g id="rulers">
				{scenario !== 'noBarriers' && <SourceToBarrierRuler />}
				{scenario !== 'noBarriers' && scenario !== 'singleBarrier' && <BarrierToBarrierRuler />}
				{scenario !== 'noBarriers' && <BarrierToReceiver />}
				<FullRuler />
			</g>
		</>
	);
};

export { Ground };